/**
 * Game store — the single source of truth that wires together the server
 * connection, the native shell (speech/location/keep-awake), and the audio
 * engine. The UI (Aberdeen) reads reactive state from here and calls action
 * methods; it never touches the bridge or the connection directly.
 *
 * Responsibilities:
 *   • Authenticate and hold the live `me` stream (user → player → game).
 *   • Register the voice + SFX socket callbacks once, and play them.
 *   • Feed GPS fixes to the server and re-attenuate looping SFX as we move.
 *   • Keep the speech grammar in sync with the player's role/actions.
 *   • Tell the shell when we're in an active game session (keep-alive).
 *   • Maintain a single chronological log of messages (voice + system + RPC
 *     errors) for the in-game CRT chat.
 */
import A from "aberdeen";
import { Connection } from "lowlander/client";
import type * as API from "../../server/api";
import type { ApiClient, ActionDef, Game, MsgEvent, SfxEvent, TalkEvent } from "./api-types";
import { bridge } from "./native/bridge";
import { config } from "./config";
import { voice } from "./audio/voice";
import { sfx } from "./audio/sfx";
import { TalkSender, TalkReceiver, preloadOpus, type TalkTransport } from "./audio/talk";
import { resumeAudio, suspendForDuplex } from "./audio/context";
import { distM } from "./geo";
import randomName from "./randomName";
import {
	setSentryPlayer,
	setSentryGame,
	setSentryContext,
	sentryBreadcrumb,
} from "./sentry";

/** A single entry in the on-screen messages log. */
export interface LogEntry {
	id: number;
	/** Wall-clock ms at which the message was logged. */
	t: number;
	/** The game phase at log time. */
	phase: string;
	/** phaseEndMs at log time, used to compute the T-/G- stamp. */
	phaseEndMs: number;
	/** Display text (voice tokens already resolved). */
	text: string;
	/** True if this is a system/RPC message, false if it was spoken aloud. */
	system: boolean;
}

export interface GeoFix {
	lat: number;
	lng: number;
	accuracy: number;
	speed: number;
	heading: number;
}

const PREF_PID = "player_id";
const PREF_NAME = "player_name";

/** How often we ping the server while connected so a stationary lobby owner
 *  isn't mistaken for absent (the server dissolves silent waiting games after
 *  ~90s). Well under that window to tolerate a couple of missed beats. */
const HEARTBEAT_MS = 29_000;

function loadOrCreateSecret(): string {
	let s = bridge.getItem(PREF_PID);
	if (!s) {
		s = (
			crypto.randomUUID?.() ??
			Math.random().toString(36).slice(2) + Date.now().toString(36)
		)
			.replace(/-/g, "")
			.slice(0, 12);
		bridge.setItem(PREF_PID, s);
	}
	return s;
}

/** Load the saved callsign, or mint a random one *and persist it* so it stays
 *  stable across reloads. Without persisting, every reload would pick a fresh
 *  random name and rename the (same) server-side user — looking like a brand
 *  new player each time. */
function loadOrCreateName(): string {
	let n = bridge.getItem(PREF_NAME);
	if (!n) {
		n = randomName();
		bridge.setItem(PREF_NAME, n);
	}
	return n;
}

class GameStore {
	readonly conn: Connection<typeof API>;
	readonly api: ApiClient;
	readonly secret: string;

	/** Reactive UI state. */
	readonly $ = A.proxy({
		name: "" as string,
		/** latest GPS fix, or null before first fix */
		fix: null as GeoFix | null,
		online: false,
		/** Full action catalogue from the server (drives lobby config UI). */
		catalog: [] as ActionDef[],
		/** Actions usable right now: catalogue ∩ role ∩ game's enabled set. */
		actions: [] as ActionDef[],
		/** Chronological log of in-game messages (voice + system). */
		log: [] as LogEntry[],
		/** True while a push-to-talk listening window is open (HUD indicator). */
		listening: false,
		/** Debug: latest native audio-route snapshot (Android only; "" elsewhere). */
		audioDebug: "" as string,
		/** Local end-screen state for a voluntary leave from an active game. */
		leftGame: false as boolean,
		lastGame: null as Game | null,
	});

	/** The authenticate() result; `.value` is the live `me` stream. */
	readonly auth: ReturnType<ApiClient["authenticate"]>;

	/** Track looping SFX origin coords so we can re-attenuate as the listener moves. */
	private loopOrigins = new Map<number, { lat: number; lng: number }>();
	/** Push-to-talk: our own mic broadcast, and the teammate transmission we're
	 *  currently receiving (only one at a time — a new one replaces the old). */
	private readonly talkSender = new TalkSender();
	private talkRx?: TalkReceiver;
	private lastRoleId: string | undefined;
	private lastStatus: string | undefined;
	private lastPhase: string | undefined;
	/** Game id whose role intro we've already announced (once per game). */
	private announcedIntroGameId: string | undefined;
	private sessionActive = false;
	/** Whether the one-time "welcome" greeting has played (after audio unlock). */
	private welcomed = false;
	private logSeq = 0;

	constructor() {
		this.secret = loadOrCreateSecret();
		this.$.name = loadOrCreateName();

		this.conn = new Connection<typeof API>(config.serverUrl);
		this.api = this.conn.api;

		// Authenticate with the current name. saveName() updates both the
		// client-side store and the server's User.name (so the name in nearby
		// game listings matches what the user just typed in the lobby).
		this.auth = this.api.authenticate(this.secret, this.$.name);

		// One-time per session: register voice + SFX + talk delivery callbacks.
		this.auth.serverProxy.subscribe(
			(msg: MsgEvent) => this.handleMsg(msg),
			(ev: SfxEvent) => this.handleSfx(ev),
			(ev: TalkEvent) => this.handleTalk(ev),
		);

		// While we're broadcasting, pause speech recognition: it would otherwise
		// fight the talk capture for the mic and hear the talker's own voice.
		this.talkSender.onActiveChange = (active) => {
			if (active) bridge.stopSpeech();
			else this.applySpeechGrammar();
		};

		// Captions for the on-screen log come from resolved voice sample text.
		// Delay by 100 ms so the auth-stream update (game phase, phaseEndMs) has
		// time to propagate before we snapshot it for the log timestamp.
		voice.onText = (text) => setTimeout(() => this.log(text, false), 100);

		// While a voice sample is playing, tell the shell so it can mute the
		// recogniser if the audio is going out over the phone's own speaker
		// (preventing our samples from triggering commands). On a headset the
		// shell leaves recognition running.
		voice.onActiveChange = (active) => bridge.setAudioPlaying(active);

		this.wireNative();
		this.wireReactive();
		this.wireSentryScope();

		// Keep the server's view of us fresh independent of GPS: a stationary
		// player emits no location fixes, but the lobby must not be dissolved out
		// from under them. Skipped while offline (queued calls would pile up).
		setInterval(() => {
			if (this.conn.isOnline()) this.auth.serverProxy.heartbeat();
		}, HEARTBEAT_MS);

		// Debug: poll the native audio-route snapshot for the in-app debug panel.
		// Cheap synchronous bridge call; "" on non-Android shells.
		setInterval(() => {
			const s = bridge.getAudioDebug();
			if (s !== this.$.audioDebug) this.$.audioDebug = s;
		}, 1000);
	}

	/** Append a line to the in-game log. Trims to the most recent 100 entries. */
	log(text: string, system = true): void {
		if (!text) return;
		const game = this.auth.value?.currentPlayer?.game;
		// Absolute phase-end ms, reconstructed from the streamed anchor + offset, so
		// the log line can show the time remaining at the moment it was recorded.
		const startTime = game?.startTime as Date | undefined;
		const phaseEndMs = startTime
			? startTime.getTime() + ((game?.phaseEndTimeOffset as number) ?? 0)
			: 0;
		const entry: LogEntry = {
			id: ++this.logSeq,
			t: Date.now(),
			phase: (game?.phase as string) ?? "",
			phaseEndMs,
			text: text.toUpperCase(),
			system,
		};
		const next = this.$.log.concat(entry);
		if (next.length > 100) next.splice(0, next.length - 100);
		this.$.log = next;
		// Mirror into Sentry's breadcrumb trail so a later crash carries the last
		// ~100 voice/system lines that led up to it.
		sentryBreadcrumb(entry.text, system ? "system" : "voice");
	}

	/** Surface a server-side RPC error to the in-game log. */
	logError(err: unknown): void {
		const msg =
			(err instanceof Error ? err.message : String(err)) || "RPC error";
		this.log("⚠ " + msg, true);
		// RPC rejections originate server-side (and are captured there), so record
		// them as a breadcrumb rather than a duplicate client-side exception.
		sentryBreadcrumb("RPC error: " + msg, "rpc");
	}

	/** Wipe the log. */
	clearLog(): void {
		this.$.log = [];
	}

	// ── Lobby ────────────────────────────────────────────────────────────
	findGames(): ReturnType<ApiClient["findGames"]> | null {
		const fix = this.$.fix;
		if (!fix) return null;
		return this.api.findGames(fix.lat, fix.lng);
	}

	// The default name used by tests (and shown in screenshots) when the test
	// shell hasn't injected its own. Picked to be short, unique, and visually
	// stable across runs. Real players get a fresh adj+noun combo.
	static readonly DEFAULT_TEST_NAME = "RECON BRAVO";

	// ── Actions exposed to the UI ──────────────────────────────────────────
	createGame() {
		this.say("creating_game");
		// Make sure the server-side user.name is current *before* the game is
		// created, so the new game's owner is labelled with the name the player
		// just typed in the lobby.
		this.auth.serverProxy.setName(this.$.name);
		return this.track(this.auth.serverProxy.createGame());
	}
	joinGame(id: string) {
		this.say("joining_game");
		this.auth.serverProxy.setName(this.$.name);
		return this.track(this.auth.serverProxy.joinGame(id));
	}
	leaveGame() {
		this.$.leftGame = true;
		this.$.lastGame = this.auth.value?.currentPlayer?.game ?? this.$.lastGame;
		this.auth.serverProxy.leaveGame();
	}
	returnToLobby() {
		// Tell the server to drop us from the (ended, or voluntarily-left) game so
		// currentPlayer clears — otherwise the auth stream keeps reporting us in a
		// phase==="ended" game and the game-over screen stays up (even across an
		// app restart, since the server still has us in it). leaveGame() is a safe
		// no-op if we're already out.
		this.auth.serverProxy.leaveGame();
		this.$.leftGame = false;
		this.$.lastGame = null;
	}
	startGame() {
		return this.track(this.auth.serverProxy.startGame());
	}
	kickPlayer(pid: string) {
		this.auth.serverProxy.kickPlayer(pid);
	}
	setRange(m: number) {
		this.auth.serverProxy.setRange(m);
	}
	setTime(s: number) {
		this.auth.serverProxy.setTime(s);
	}
	/** Owner-only: toggle an action on/off for the waiting game. */
	setActionEnabled(word: string, on: boolean) {
		this.auth.serverProxy.setActionEnabled(word, on);
	}
	invokeAction(id: number) {
		// Audio-stream actions (push-to-talk) are driven entirely client-side: we
		// capture the mic and stream to teammates rather than issuing a server
		// invoke. The action stays server-agnostic on the client — we only key off
		// the catalogue's generic `stream` flag.
		const a =
			this.$.actions.find((x) => x.id === id) ??
			this.$.catalog.find((x) => x.id === id);
		if (a?.stream === "audio") {
			void this.talkSender.begin(this.talkTransport);
			return;
		}
		return this.track(this.auth.serverProxy.invokeAction(id));
	}

	/** RPC-backed transport the talk sender streams through. */
	private readonly talkTransport: TalkTransport = {
		start: () =>
			(this.auth.serverProxy.talkStart().promise as Promise<unknown>)
				.then((v) => v === true)
				.catch(() => false),
		data: (bytes, ts) => {
			this.auth.serverProxy.talkData(bytes, ts);
		},
		end: () => {
			this.auth.serverProxy.talkEnd();
		},
	};
	saveName(name: string) {
		name = name.trim();
		if (!name) return;
		this.$.name = name;
		bridge.setItem(PREF_NAME, name);
		this.auth.serverProxy.setName(name);
	}

	/** Promise-track an RPC: surface rejections to the in-game log. */
	private track<T>(pp: any): any {
		if (!pp) return pp;
		const p = pp.promise as Promise<T> | undefined;
		if (p && typeof p.then === "function") p.catch((e) => this.logError(e));
		return pp;
	}

	/** Manually fire a local voice cue (welcome, ok, error…). */
	say(...tokens: string[]) {
		voice.speak(tokens);
	}

	// ── Native wiring ──────────────────────────────────────────────────────
	private wireNative() {
		bridge.on("location", ({ fix }) => {
			this.$.fix = fix;
			// Push to server (heading + accuracy included so the server need not derive them).
			this.auth.serverProxy.setLocation(fix.lat, fix.lng, fix.heading, fix.accuracy);
			// Re-attenuate active loops as our distance to each source changes.
			sfx.reattenuateAll((sid) => {
				const o = this.loopOrigins.get(sid);
				if (!o) return null;
				return distM(fix.lat, fix.lng, o.lat, o.lng);
			});
		});

		bridge.on("speechCommand", (e) => this.onVoiceCommand(e.word));
		// Push-to-talk: recognition only runs inside a window opened by a headset
		// play/pause press. Mirror the state for the HUD's LISTENING indicator, and
		// park all WebAudio while the SCO link is up: parking releases the output
		// stream so the A2DP→SCO route switch can't kill the context (the "everything
		// goes silent" bug), and playing sounds freeze and resume at the same offset
		// afterwards. Voice is also held so a reply queues rather than being chopped
		// by the switch. (No audio "Ready" cue — it would route to the phone speaker
		// mid-switch; the HUD says SPEAK NOW instead.)
		bridge.on("speechListening", (e) => {
			this.$.listening = e.active;
			if (e.active) {
				voice.setHold(true);
				suspendForDuplex(true);
			}
			// The release is driven by `duplexReleased`, not by the window closing:
			// the reply must wait until the headset is actually back on A2DP, else it
			// starts on the tail of the SCO→A2DP switch and gets chopped.
		});
		// SCO is down and the media (A2DP) route is back — unpark audio and flush the
		// queued reply. We release immediately on the native signal and only ignore a
		// stale event if a new listening window has reopened after the release.
		bridge.on("duplexReleased", () => {
			if (this.$.listening) return;
			suspendForDuplex(false);
			voice.setHold(false);
		});
		bridge.on("audioFocus", (e) => {
			if (!e.hasFocus) voice.clear();
		});

		bridge.startLocation();
	}

	private onVoiceCommand(word: string) {
		const a = this.$.actions.find((x) => x.word === word);
		if (a) {
			// No local "ok" cue — the server sends the command's feedback.
			this.invokeAction(a.id);
		} else {
			this.say("error_cue");
		}
	}

	/** Sync the speech recogniser's grammar to the current usable action set.
	 *  Skipped while we're broadcasting, so talk capture keeps the mic to itself. */
	private applySpeechGrammar() {
		if (this.talkSender.isActive()) return;
		const list = this.$.actions;
		if (list.length) bridge.startSpeech(list.map((a) => a.word));
		else bridge.stopSpeech();
	}

	// ── Server-push handlers ────────────────────────────────────────────────
	private handleMsg(msg: MsgEvent) {
		if (!msg?.tokens?.length) return;
		voice.speak(msg.tokens, msg.intraGapMs ?? 0);
	}

	private handleSfx(ev: SfxEvent) {
		const fix = this.$.fix;
		const dist =
			fix && ev.lat != null && ev.lng != null
				? distM(fix.lat, fix.lng, ev.lat, ev.lng)
				: 0;

		if (ev.action === "stop") {
			console.log(`[sfx] stop sid=${ev.sid}`);
			this.loopOrigins.delete(ev.sid);
			sfx.stopLoop(ev.sid);
			return;
		}
		if (ev.action === "start" || ev.looping) {
			console.log(`[sfx] start loop sid=${ev.sid} name=${ev.name}`);
			if (ev.lat != null && ev.lng != null)
				this.loopOrigins.set(ev.sid, { lat: ev.lat, lng: ev.lng });
			void sfx.startLoop(ev.sid, ev.name, dist, ev.looping !== false);
			return;
		}
		// one-shot
		void sfx.playOneShot(ev.name, dist);
	}

	/** A teammate's push-to-talk transmission, reassembled + played in real time. */
	private handleTalk(ev: TalkEvent) {
		if (ev.kind === "begin") {
			// Replace any still-running transmission with the new one.
			this.talkRx?.dispose();
			this.talkRx = new TalkReceiver(() => {
				this.talkRx = undefined;
			});
		} else if (ev.kind === "data") {
			this.talkRx?.push(ev.data, ev.ts);
		} else {
			this.talkRx?.end();
		}
	}

	// ── Reactive glue ───────────────────────────────────────────────────────
	private wireReactive() {
		// Connection status mirror.
		A(() => {
			this.$.online = this.conn.isOnline();
		});

		// Push the cached GPS fix to the server the moment we get a Player so
		// the server has our real coordinates even if GPS hasn't fired a new event
		// since we joined (e.g. stationary users whose watchPosition fired while
		// still in the lobby, before currentPlayer existed).
		let knownPlayerId: string | undefined;
		A(() => {
			const pid = this.auth.value?.currentPlayer?.id;
			if (pid && pid !== knownPlayerId) {
				knownPlayerId = pid;
				const fix = this.$.fix;
				if (fix)
					this.auth.serverProxy.setLocation(
						fix.lat,
						fix.lng,
						fix.heading,
						fix.accuracy,
					);
			}
			if (!pid) knownPlayerId = undefined;
		});

		A(() => {
			const game = this.auth.value?.currentPlayer?.game;
			if (game) this.$.lastGame = game as Game;
		});

		// Fetch the full action catalogue once; the client renders entirely from it.
		const catReq = this.auth.serverProxy.getActionCatalog();
		A(() => {
			const c = catReq.value;
			if (c) this.$.catalog = c as unknown as ActionDef[];
		});

		// Recompute the usable action set from catalogue ∩ role ∩ game's enabled set.
		// Fine-grained reactivity: only re-runs when role / enabledActions / catalogue
		// change — not on every credits/countdown stream tick.
		A(() => {
			const player = this.auth.value?.currentPlayer;
			const roleId = player?.roleId;
			if (roleId !== this.lastRoleId) {
				const wasRole = this.lastRoleId;
				this.lastRoleId = roleId;
				if (roleId && wasRole !== undefined) this.log(`role: ${roleId}`, true);
			}
			// Voice/tiles only make sense while a game is actually live AND we're
			// still in it. After it ends the player keeps a roleId, and a dead
			// player is a spectator — without these gates the recogniser would keep
			// listening on the game-over screen.
			const phase = player?.game?.phase;
			const dead = player?.statusLabel === "Dead";
			const active = (phase === "grace" || phase === "running") && !dead;
			if (!active || !roleId) {
				this.$.actions = [];
				bridge.stopSpeech();
				return;
			}
			const enabled = player?.game?.enabledActions ?? [];
			const enabledSet = new Set(enabled);
			const list = this.$.catalog.filter(
				(a) =>
					a.roles.some((r) => r.id === roleId) &&
					(enabled.length ? enabledSet.has(a.word) : a.tier === "basic"),
			);
			this.$.actions = list;
			this.applySpeechGrammar();
		});

		// Status changes → emit a system log line.
		A(() => {
			const status = this.auth.value?.currentPlayer?.statusLabel;
			const key = "status:" + (status ?? "");
			if (key === this.lastStatus) return;
			this.lastStatus = key;
			if (status) this.log(`status: ${status}`, true);
		});

		// Whether we're in an active game session: a player who's joined a game
		// that hasn't finished (waiting/grace/running) — NOT the start page (no
		// player) and NOT the game-over screen (ended). This is the single signal
		// that keeps the background pipeline (GPS + mic + audio) alive: the native
		// shell runs the audio stack whenever the app is foregrounded OR this is
		// set, so a backgrounded phone keeps playing through a live game but goes
		// quiet on the home/result screens. (Broader than the old grace/running
		// keep-awake so the lobby works backgrounded too.)
		A(() => {
			const phase = this.auth.value?.currentPlayer?.game?.phase;
			const active =
				phase === "waiting" || phase === "grace" || phase === "running";
			if (active !== this.sessionActive) {
				this.sessionActive = active;
				bridge.setKeepAwake(active);
			}
		});

		// Push-to-talk only runs during the hunt — tear down any in-progress
		// broadcast/playback the moment the game stops running (left, died-out,
		// ended), so the mic is released and a stale receiver can't hang.
		A(() => {
			const running =
				this.auth.value?.currentPlayer?.game?.phase === "running";
			if (running) {
				// Warm the Opus wasm so the first broadcast/playback isn't delayed
				// by the fetch+instantiate.
				preloadOpus();
			} else {
				this.talkSender.cancel();
				this.talkRx?.dispose();
				this.talkRx = undefined;
			}
		});

		// Catch-all spatial-SFX teardown: the moment the game stops being live
		// (ended / left / disconnected), kill every looping sound and forget its
		// origin. The server already sends a per-loop `stop`, but a stop that
		// races a still-loading start can be missed — and an orphaned loop keeps
		// re-panning/attenuating on every GPS fix, which looks exactly like "the
		// old game is still running" (a bomb that beeps on after game over, sound
		// that still swells with distance). This guarantees silence regardless.
		A(() => {
			const phase = this.auth.value?.currentPlayer?.game?.phase;
			const live = phase === "grace" || phase === "running";
			if (!live) {
				sfx.stopAll();
				this.loopOrigins.clear();
			}
		});

		// Reset the message log when a new game begins (entering grace) and
		// emit a single intro line describing the player's role + the head-start
		// count-down, so the on-screen log is never an empty "— NO TRANSMISSIONS —".
		A(() => {
			const phase = this.auth.value?.currentPlayer?.game?.phase;
			if (!phase && this.lastPhase) this.clearLog();
			this.lastPhase = phase;
		});

		// Announce the role intro ("You are a hunter…") once per game, derived from
		// streamed state rather than a one-time server push — so it still fires after
		// a late join/reconnect that connects a moment after the game started.
		A(() => {
			const player = this.auth.value?.currentPlayer;
			const game = player?.game;
			const roleId = player?.roleId;
			if (
				game?.phase === "grace" &&
				(roleId === "hunter" || roleId === "runner") &&
				game.id !== this.announcedIntroGameId
			) {
				this.announcedIntroGameId = game.id;
				// Voice-sample id derives straight from the role id (role_hunter /
				// role_runner) — no hard-coded role→sample mapping on the client.
				this.say("role_" + roleId);
			}
		});
	}

	/** Keep Sentry's scope in step with who we are and what we're doing, so every
	 *  crash report carries the player, game phase/role and link state — and one
	 *  static block describing the shell (native Android vs plain browser). */
	private wireSentryScope() {
		const caps = bridge.capabilities();
		setSentryContext("shell", {
			speech: caps.speech,
			location: caps.location,
			keepAwake: caps.keepAwake,
			native: caps.speech || caps.location || caps.keepAwake,
			userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
		});

		// Player identity: the per-game Player id (when in a game) plus the stable
		// callsign. Fine-grained so it only re-runs when the player actually changes.
		A(() => {
			const player = this.auth.value?.currentPlayer;
			setSentryPlayer({ userId: player?.id, name: this.$.name });
		});
		// Coarse gameplay tags for grouping/filtering issues in Bugsink.
		A(() => {
			const player = this.auth.value?.currentPlayer;
			const game = player?.game;
			setSentryGame({
				phase: game?.phase,
				role: player?.roleId,
				gameId: game?.id,
				online: this.$.online,
			});
		});
	}

	/** Unlock audio playback (from startup on the autoplay-capable WebView, or
	 *  from the first user gesture elsewhere) and greet the player once — but only
	 *  once audio is genuinely running, so a still-suspended context doesn't burn
	 *  the one-shot greeting before it can be heard. */
	async unlockAudio() {
		const running = await resumeAudio();
		if (running && !this.welcomed) {
			this.welcomed = true;
			this.say("welcome");
		}
	}
}

export const store = new GameStore();
